import React from 'react';
import { ChevronLeft, Star, MessageCircle, Phone } from 'lucide-react';
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Heading, 
  Button,
  Badge,
  Card,
  CardBody,
  Avatar,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';

interface Position {
  id: string;
  title: string;
  department: string;
  experience: string;
  skills: string[];
  rating: number;
  avatar: string;
}

const getPositionsForIntegration = (integration: string): Position[] => {
  const positionsMap: Record<string, Position[]> = {
    salesforce: [
      {
        id: 'sales-manager',
        title: 'Менеджер по продажам',
        department: 'Отдел продаж',
        experience: '5+ лет',
        skills: ['CRM', 'B2B продажи', 'Переговоры'],
        rating: 4.8,
        avatar: '👨‍💼'
      },
      {
        id: 'sales-admin',
        title: 'Администратор Salesforce',
        department: 'IT отдел',
        experience: '3+ года',
        skills: ['Salesforce Admin', 'Apex', 'Lightning'],
        rating: 4.9,
        avatar: '👩‍💻'
      }
    ],
    sap: [
      {
        id: 'sap-consultant',
        title: 'SAP Консультант',
        department: 'IT консалтинг',
        experience: '7+ лет',
        skills: ['SAP FICO', 'SAP MM', 'ABAP'],
        rating: 4.7,
        avatar: '👨‍🔬'
      },
      {
        id: 'business-analyst',
        title: 'Бизнес-аналитик',
        department: 'Аналитика',
        experience: '4+ года',
        skills: ['Бизнес-процессы', 'SAP', 'Аналитика'],
        rating: 4.6,
        avatar: '👩‍💼'
      }
    ],
    workday: [
      {
        id: 'hr-manager',
        title: 'HR Менеджер',
        department: 'HR отдел',
        experience: '5+ лет',
        skills: ['HR процессы', 'Workday HCM', 'Рекрутинг'],
        rating: 4.8,
        avatar: '👩‍💼'
      },
      {
        id: 'payroll-specialist',
        title: 'Специалист по зарплате',
        department: 'HR отдел',
        experience: '3+ года',
        skills: ['Workday Payroll', 'Налоги', 'Отчеты'],
        rating: 4.5,
        avatar: '👨‍💼'
      }
    ],
    hubspot: [
      {
        id: 'marketing-manager',
        title: 'Маркетинг менеджер',
        department: 'Маркетинг',
        experience: '4+ года',
        skills: ['Digital Marketing', 'HubSpot', 'Analytics'],
        rating: 4.7,
        avatar: '👩‍🎨'
      }
    ],
    jira: [
      {
        id: 'project-manager',
        title: 'Проект менеджер',
        department: 'Управление проектами',
        experience: '6+ лет',
        skills: ['Agile', 'Scrum', 'Jira Admin'],
        rating: 4.8,
        avatar: '👨‍💼'
      }
    ],
    servicenow: [
      {
        id: 'it-admin',
        title: 'ИТ Администратор',
        department: 'ИТ поддержка',
        experience: '4+ года',
        skills: ['ServiceNow Admin', 'ITIL', 'Workflows'],
        rating: 4.6,
        avatar: '👨‍💻'
      }
    ]
  };

  return positionsMap[integration] || [];
};

interface PositionsPageProps {
  integration: string;
  onPositionSelect: (position: string) => void;
  onBack: () => void;
}

export function PositionsPage({ integration, onPositionSelect, onBack }: PositionsPageProps) {
  const positions = getPositionsForIntegration(integration);
  const bgGradient = useColorModeValue(
    'linear(to-b, blue.50, white)', 
    'linear(to-b, gray.900, gray.800)'
  );
  const headerBg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  
  const integrationNames: Record<string, string> = {
    salesforce: 'Salesforce',
    sap: 'SAP ERP',
    workday: 'Workday',
    hubspot: 'HubSpot',
    jira: 'Jira',
    servicenow: 'ServiceNow'
  };

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      {/* Header */}
      <Box bg={headerBg} shadow="sm" p={4}>
        <Box maxW="md" mx="auto">
          <HStack align="center">
            <Button variant="ghost" size="sm" onClick={onBack} mr={3}>
              <Icon as={ChevronLeft} w={5} h={5} />
            </Button>
            <VStack align="start" spacing={0}>
              <Heading size="md" fontWeight="semibold" color={textColor}>
                Выбор специалиста
              </Heading>
              <Text fontSize="sm" color={mutedColor}>
                {integrationNames[integration]}
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Box>

      {/* Positions List */}
      <Box maxW="md" mx="auto" p={4}>
        <VStack spacing={4}>
          {positions.map((position) => (
            <Card
              key={position.id}
              bg={cardBg}
              borderWidth={2}
              borderColor="transparent"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ 
                shadow: "lg",
                borderColor: "blue.200"
              }}
              w="full"
            >
              <CardBody p={4}>
                <HStack align="start" spacing={4}>
                  <Avatar
                    size="md"
                    bg="linear-gradient(135deg, #3b82f6, #8b5cf6)"
                    color="white"
                    name={position.avatar}
                    src=""
                    flexShrink={0}
                  >
                    {position.avatar}
                  </Avatar>
                  <VStack align="start" flex={1} spacing={2}>
                    <HStack justify="space-between" w="full" align="start">
                      <Heading size="sm" fontWeight="medium" color={textColor} wordBreak="break-word" flex={1} mr={2}>
                        {position.title}
                      </Heading>
                      <HStack spacing={1} flexShrink={0}>
                        <Icon as={Star} w={4} h={4} color="yellow.400" fill="currentColor" />
                        <Text fontSize="sm" color={mutedColor}>{position.rating}</Text>
                      </HStack>
                    </HStack>
                    
                    <Text fontSize="sm" color={mutedColor} wordBreak="break-word">
                      {position.department}
                    </Text>
                    
                    <Text fontSize="xs" color={mutedColor} mb={3}>
                      Опыт: {position.experience}
                    </Text>
                    
                    <HStack wrap="wrap" spacing={1} mb={4} align="start">
                      {position.skills.map((skill, index) => (
                        <Badge key={index} colorScheme="gray" fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                          {skill}
                        </Badge>
                      ))}
                    </HStack>

                    <HStack spacing={2} w="full">
                      <Button 
                        size="sm" 
                        colorScheme="blue"
                        flex={1}
                        leftIcon={<Icon as={MessageCircle} w={4} h={4} />}
                        onClick={() => onPositionSelect(position.id)}
                      >
                        Чат
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        colorScheme="blue"
                        flex={1}
                        leftIcon={<Icon as={Phone} w={4} h={4} />}
                        onClick={() => onPositionSelect(position.id)}
                      >
                        Звонок
                      </Button>
                    </HStack>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}